import { EPSILON, formatMm } from '@bw/core';
import type { Viewport } from './viewport.js';
import type { DrawTheme } from './theme.js';

/** SPECIFICATION.md clause 11.5 — the adaptive grid ladder. */
export const GRID_STEPS = [
  10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10_000,
  20_000, 50_000, 100_000, 200_000, 500_000, 1_000_000,
] as const;

/** The first step whose on-screen spacing is at least 28 px. */
export function gridStepFor(scale: number): number {
  return GRID_STEPS.find((step) => step * scale >= 28) ?? (GRID_STEPS[GRID_STEPS.length - 1] as number);
}

const MAX_LINES = 500;

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
): void {
  const width = viewport.width;
  const height = viewport.height;
  const step = gridStepFor(viewport.scale);
  const major = step * 5;
  const left = viewport.toWorldX(0);
  const right = viewport.toWorldX(width);
  const bottom = viewport.toWorldY(height);
  const top = viewport.toWorldY(0);

  ctx.save();
  ctx.lineWidth = 1;
  ctx.font = '10px ui-monospace, SFMono-Regular, Consolas, monospace';
  ctx.fillStyle = theme.gridLabel;

  const isMajor = (value: number): boolean =>
    Math.abs(value / major - Math.round(value / major)) < EPSILON;

  let x = Math.floor(left / step) * step;
  for (let n = 0; x <= right + EPSILON && n < MAX_LINES; n++, x += step) {
    const sx = Math.round(viewport.toScreenX(x)) + 0.5;
    const axis = Math.abs(x) < EPSILON;
    const majorLine = isMajor(x);
    ctx.strokeStyle = axis ? theme.gridAxis : majorLine ? theme.gridMajor : theme.gridMinor;
    ctx.lineWidth = axis ? 2 : 1;
    ctx.beginPath();
    ctx.moveTo(sx, 0);
    ctx.lineTo(sx, height);
    ctx.stroke();
    if (majorLine && !axis) {
      const labelY = Math.min(height - 6, Math.max(14, viewport.originY + 14));
      ctx.fillText(formatMm(x), sx + 3, labelY);
    }
  }

  let y = Math.floor(bottom / step) * step;
  for (let n = 0; y <= top + EPSILON && n < MAX_LINES; n++, y += step) {
    const sy = Math.round(viewport.toScreenY(y)) + 0.5;
    const axis = Math.abs(y) < EPSILON;
    const majorLine = isMajor(y);
    ctx.strokeStyle = axis ? theme.gridAxis : majorLine ? theme.gridMajor : theme.gridMinor;
    ctx.lineWidth = axis ? 2 : 1;
    ctx.beginPath();
    ctx.moveTo(0, sy);
    ctx.lineTo(width, sy);
    ctx.stroke();
    if (majorLine && !axis) {
      const labelX = Math.min(width - 40, Math.max(4, viewport.originX + 5));
      ctx.fillText(formatMm(y), labelX, sy - 4);
    }
  }

  ctx.fillStyle = theme.gridCaption;
  ctx.textAlign = 'right';
  ctx.fillText(`Grid ${formatMm(step)} mm  ·  Drawing units: mm`, width - 10, height - 9);
  ctx.restore();
}
